import type { ReactNode } from "react";

export interface NavItem {
    readonly id: string;
    readonly label: string;
    /** Absent on a group header, which hands off to its first child. */
    readonly href?: string;
    readonly icon?: ReactNode;
    /** Defaults to visible. A hidden row is dropped, not greyed. */
    readonly visible?: boolean;
    readonly locked?: boolean;
    readonly lockedTooltip?: string;
    readonly count?: number;
    readonly children?: readonly NavItem[];
}

/** A group whose children are all hidden is dropped, not rendered as an empty header. */
export function visibleNavItems<
    T extends { visible?: boolean; children?: readonly T[] },
>(items: readonly T[]): readonly T[] {
    return items
        .filter((item) => item.visible !== false)
        .map((item) =>
            item.children === undefined
                ? item
                : { ...item, children: visibleNavItems(item.children) }
        )
        .filter(
            (item) => item.children === undefined || item.children.length > 0
        );
}

/** Hands a group header off to its first descendant. A locked row has none. */
export function navDestination<
    T extends { href?: string; locked?: boolean; children?: readonly T[] },
>(item: T): string | undefined {
    if (item.locked === true) {
        return undefined;
    }

    return item.href ?? item.children?.map(navDestination).find(Boolean);
}

/** The rows from a root down to `id`. Empty when no row carries it. */
export function navTrail<T extends { id: string; children?: readonly T[] }>(
    items: readonly T[],
    id: string
): readonly T[] {
    return (
        items
            .map((item) => {
                if (item.id === id) {
                    return [item];
                }
                const below = navTrail(item.children ?? [], id);
                return below.length === 0 ? [] : [item, ...below];
            })
            .find((trail) => trail.length > 0) ?? []
    );
}
